import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://sy47l7fjif.execute-api.us-east-1.amazonaws.com/Prod";
const PAGE_SIZE = 50;

const Badge = ({ children, kind = "default" }) => {
  const base =
    "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap";
  const styles = {
    default: "bg-gray-100 text-gray-700 border-gray-200",
    green: "bg-green-100 text-green-700 border-green-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    yellow: "bg-yellow-100 text-yellow-700 border-yellow-200",
    red: "bg-red-100 text-red-700 border-red-200",
    orange: "bg-orange-100 text-orange-700 border-orange-200",
    purple: "bg-purple-100 text-purple-700 border-purple-200",
  };

  return (
    <span className={`${base} ${styles[kind] || styles.default}`}>
      {children || "-"}
    </span>
  );
};


const StatCard = ({ title, value, hint, kind = "default" }) => {
  const accents = {
    default: "from-gray-50 to-white border-gray-200",
    blue: "from-blue-50 to-white border-blue-200",
    green: "from-green-50 to-white border-green-200",
    yellow: "from-yellow-50 to-white border-yellow-200",
    red: "from-red-50 to-white border-red-200",
  };

  return (
    <div
      className={`rounded-2xl border bg-gradient-to-br ${
        accents[kind] || accents.default
      } p-6 shadow-sm`}
    >
      <div className="text-sm text-gray-500">{title}</div>
      <div className="mt-3 text-3xl font-bold text-gray-900">{value}</div>
      {hint ? <div className="mt-2 text-sm text-gray-500">{hint}</div> : null}
    </div>
  );
};

const EmptyState = ({ title, text }) => (
  <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center">
    <div className="text-lg font-semibold text-gray-800">{title}</div>
    <p className="mt-2 text-sm text-gray-500">{text}</p>
  </div>
);

const formatCurrency = (amount, currency = "EUR") => {
  const numeric = Number(amount || 0);
  try {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR",
    }).format(numeric);
  } catch {
    return `${numeric.toFixed(2)} ${currency || "EUR"}`;
  }
};

const formatDate = (dateValue) => {
  if (!dateValue) return "-";

  const raw = String(dateValue).trim();

  const deMatch = raw.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})$/);
  if (deMatch) {
    const [, d, m, y] = deMatch;
    const fullYear = y.length === 2 ? `20${y}` : y;
    return `${String(d).padStart(2, "0")}.${String(m).padStart(
      2,
      "0"
    )}.${fullYear}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString("de-DE");
};

const formatDateTime = (dateValue) => {
  if (!dateValue) return "-";
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return dateValue;
  return d.toLocaleString("de-DE");
};

const getCompanyId = () => {
  const direct = localStorage.getItem("company_id");
  if (direct) return direct;

  try {
    const ctxRaw = localStorage.getItem("company_context");
    if (!ctxRaw) return "";
    const ctx = JSON.parse(ctxRaw);
    return ctx.company_id || "";
  } catch {
    return "";
  }
};

const buildQuery = (params) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

const normalizeNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return value;

  let text = String(value).trim();
  if (!text) return 0;

  text = text.replace(/€/g, "").replace(/EUR/gi, "").replace(/\s/g, "");

  if (text.includes(",") && text.includes(".")) {
    text = text.replace(/\./g, "").replace(",", ".");
  } else if (text.includes(",")) {
    text = text.replace(",", ".");
  }

  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeBool = (value) => {
  if (typeof value === "boolean") return value;
  if (value === null || value === undefined) return false;

  const text = String(value).trim().toLowerCase();
  return [
    "true",
    "1",
    "yes",
    "ja",
    "y",
    "geschlossen",
    "abgelegt",
    "closed",
    "erledigt",
    "abgeschlossen",
  ].includes(text);
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const normalizeAz = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const getCaseAz = (item = {}) =>
  normalizeAz(
    item.az ||
      item.AZ ||
      item.aktenzeichen ||
      item.akten_zeichen ||
      item.aktzeichen ||
      item.case_id ||                // 🔥 DAS FEHLT BEI DIR
      item.mandant_az ||
      item.mandanten_az ||
      item.mandanten_aktenzeichen ||
      ""
  );

const getCaseYear = (item = {}) => getCaseAz(item).slice(0, 4);

const getSortableDateValue = (value) => {
  if (!value) return 0;

  const raw = String(value).trim();

  const deMatch = raw.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})$/);
  if (deMatch) {
    const [, d, m, y] = deMatch;
    const fullYear = y.length === 2 ? `20${y}` : y;
    const iso = `${fullYear}-${String(m).padStart(2, "0")}-${String(d).padStart(
      2,
      "0"
    )}`;
    const ts = new Date(iso).getTime();
    return Number.isNaN(ts) ? 0 : ts;
  }

  const ts = new Date(raw).getTime();
  return Number.isNaN(ts) ? 0 : ts;
};

const sortCases = (items, sortBy) => {
  const sorted = [...items];

  sorted.sort((a, b) => {
    switch (sortBy) {
      case "az_asc":
        return normalizeText(getCaseAz(a)).localeCompare(normalizeText(getCaseAz(b)), "de");
      case "az_desc":
        return normalizeText(getCaseAz(b)).localeCompare(normalizeText(getCaseAz(a)), "de");

      case "schuldner_asc":
        return normalizeText(a.schuldner).localeCompare(
          normalizeText(b.schuldner),
          "de"
        );
      case "schuldner_desc":
        return normalizeText(b.schuldner).localeCompare(
          normalizeText(a.schuldner),
          "de"
        );

      case "offene_hf_desc":
        return Number(b.offene_hf || 0) - Number(a.offene_hf || 0);
      case "offene_hf_asc":
        return Number(a.offene_hf || 0) - Number(b.offene_hf || 0);

      case "nk_desc":
        return Number(b.nk || 0) - Number(a.nk || 0);
      case "nk_asc":
        return Number(a.nk || 0) - Number(b.nk || 0);

      case "last_event_date_asc":
        return (
          getSortableDateValue(a.last_event_date) -
          getSortableDateValue(b.last_event_date)
        );

      case "last_event_date_desc":
      default:
        return (
          getSortableDateValue(b.last_event_date) -
          getSortableDateValue(a.last_event_date)
        );
    }
  });

  return sorted;
};

const getStadiumKind = (value) => {
  const text = String(value || "").trim().toLowerCase();

  if (text.includes("vorgericht")) return "yellow";
  if (text.includes("mahnbescheid")) return "orange";
  if (text.includes("vollstreck")) return "red";
  if (text.includes("insol")) return "purple";
  if (text.includes("raten")) return "blue";
  if (text.includes("abgeschlossen") || text.includes("erledigt")) return "green";

  return "default";
};

const getStatusKind = (value) => {
  const text = String(value || "").trim().toLowerCase();

  if (text.includes("offen") || text.includes("neu") || text.includes("aktiv")) {
    return "blue";
  }

  if (
    text.includes("erledigt") ||
    text.includes("abgeschlossen") ||
    text.includes("geschlossen")
  ) {
    return "green";
  }

  if (text.includes("pause") || text.includes("wart")) {
    return "yellow";
  }

  return "default";
};

const getBearbeitungsstatusKind = (value) => {
  const text = String(value || "").trim().toLowerCase();

  if (text.includes("aktiv")) return "blue";
  if (text.includes("wart")) return "yellow";
  if (text.includes("überwachung") || text.includes("ueberwachung")) return "purple";
  if (text.includes("prüfen") || text.includes("pruefen")) return "orange";
  if (text.includes("abgeschlossen")) return "green";
  if (text.includes("unklar")) return "default";

  return "default";
};

const normalizeCase = (item = {}, index = 0) => ({
  case_id:
    item.case_id ||
    item.id ||
    item.az ||
    item.aktenzeichen ||
    item.mandant_az ||
    `case-${index}`,

  az: getCaseAz(item),

  mandant_az:
    item.mandant_az ||
    item.mandanten_az ||
    item.mandanten_aktenzeichen ||
    item.mandatsreferenz ||
    item.mandatsnummer ||
    "",

  schuldner:
    item.schuldner ||
    item.schuldner_name ||
    item.schuldner_full_name ||
    item.debtor_name ||
    item.debtor ||
    item.debtor_full_name ||
    item.person_name ||
    item.firma ||
    item.firmenname ||
    "",

  ort:
    item.schuldner_ort ||
    item.ort ||
    item.stadt ||
    item.wohnort ||
    item.city ||
    "",

  stadium:
    item.stadium ||
    item.verfahrensstand ||
    item.status_stadium ||
    item.aktenstatus ||
    item.stage ||
    "",

  bearb_status:
    item.bearb_status ||
    item.status ||
    "",

  bearbeitungsstatus:
    item.bearbeitungsstatus ||
    item.bearbeitungs_status ||
    "",

  status_text:
    item.status_text ||
    "",

  offen_text:
    item.offen_text ||
    "",

  letzter_vorgang_datum:
    item.letzter_vorgang_datum ||
    item.letzter_vorgang ||
    item.last_event_date ||
    "",

  tage_seit_letztem_vorgang:
    item.tage_seit_letztem_vorgang ??
    item.days_since_last_event ??
    null,

  offene_hf: normalizeNumber(
    item.offene_hf ??
      item.offene_hauptforderung ??
      item.hauptforderung_offen ??
      item.saldo_offen ??
      item.saldo ??
      item.offen ??
      item.betrag ??
      item.hauptforderung ??
      item.principal_amount ??
      0
  ),

  uebern_hf: normalizeNumber(
    item.uebern_hf ??
      item.uebernommene_hf ??
      item.uebernommene_hauptforderung ??
      0
  ),

  uebern_kosten: normalizeNumber(
    item.uebern_kosten ??
      item.uebernommene_kosten ??
      item.kosten ??
      item.auslagen ??
      0
  ),

  nk: normalizeNumber(
    item.nk ??
      item.NK ??
      item.nebenkosten ??
      item.nebenforderung ??
      item.neben_forderung ??
      0
  ),

  vadatum: item.vadatum || item.va_datum || "",
  evdatum: item.evdatum || item.ev_datum || "",

  last_event_text:
    item.last_event_text ||
    item.kurztext_sachstand ||
    item.kurzttext_sachstand ||
    item.sachstand ||
    item.letztes_ereignis ||
    item.ereignistext ||
    item.letzter_stand ||
    item.status_text ||
    "",

  last_event_date:
    item.last_event_date ||
    item.uebernahmedatum ||
    item.letztes_ereignis_datum ||
    item.ereignisdatum ||
    item.datum ||
    item.updated_at ||
    item.last_update ||
     null,

imported_at:
     item.imported_at ||
     item.import_at ||
     item.importedAt ||
     item.last_import_at ||
     item.last_imported_at ||
     item.created_at ||
    null,

  abschlussdatum: item.abschlussdatum || "",

  is_closed:
    normalizeBool(
      item.is_closed ??
        item.geschlossen ??
        item.abgelegt ??
        item.closed ??
        item.abschlussdatum
    ) ||
    ["geschlossen", "abgeschlossen", "erledigt"].includes(
      String(item.bearb_status || item.status || "").toLowerCase()
    ),
});

const normalizeStats = (raw = {}, normalizedCases = []) => {
  // 🔹 Fallback: letztes Importdatum aus Cases bestimmen
  const fallbackLastImport = normalizedCases.reduce((latest, item) => {
    const dateValue =
      item.imported_at ||
      item.last_imported_at ||
      item.updated_at ||
      item.last_event_date ||
      null;

    if (!dateValue) return latest;

    const current = new Date(dateValue).getTime();
    const prev = latest ? new Date(latest).getTime() : 0;

    return current > prev ? dateValue : latest;
  }, null);

  return {
    offeneAkten:
      raw.offeneAkten ??
      raw.open_cases ??
      raw.total_cases ??
      raw.returned_cases ??
      raw.count ??
      normalizedCases.filter((item) => !item.is_closed).length ??
      0,

    offeneHf:
      raw.offeneHf ??
      raw.offene_hf ??
      raw.total_hf ??
      raw.open_principal ??
      raw.total_open_amount ??
      normalizedCases.reduce((sum, item) => sum + Number(item.offene_hf || 0), 0),

    uebernommeneHf:
      raw.uebernommeneHf ??
      raw.uebern_hf ??
      raw.total_uebern_hf ??
      normalizedCases.reduce((sum, item) => sum + Number(item.uebern_hf || 0), 0),

    uebernommeneKosten:
      raw.uebernommeneKosten ??
      raw.uebern_kosten ??
      raw.total_uebern_kosten ??
      normalizedCases.reduce((sum, item) => sum + Number(item.uebern_kosten || 0), 0),

    nkGesamt:
      raw.nkGesamt ??
      raw.nk_gesamt ??
      raw.total_nk ??
      raw.total_nk_amount ??
      normalizedCases.reduce((sum, item) => sum + Number(item.nk || 0), 0),

    letzteAktualisierung:
      raw.letzteAktualisierung ??
      raw.last_updated_at ??
      raw.last_import_at ??
      raw.updated_at ??
      fallbackLastImport ??
      null,
  };
};

const applyClientFilters = ({
  items = [],
  search = "",
  stadium = "",
  bearbeitungsstatus = "",
  yearFilter = "",
  nurOffene = true,
  sortBy = "last_event_date_desc",
}) => {
  const normalizedSearch = normalizeText(search);

  const filtered = items.filter((item) => {
    const caseAz = getCaseAz(item);
    const caseYear = getCaseYear(item);

    const haystack = [
      caseAz,
      item.mandant_az,
      item.schuldner,
      item.ort,
      item.stadium,
      item.bearb_status,
      item.bearbeitungsstatus,
      item.status_text,
      item.last_event_text,
      item.nk,
    ]
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map((value) => String(value).trim())
      .join(" ")
      .toLowerCase();

    const matchesSearch =
      !normalizedSearch ||
      haystack.includes(normalizedSearch) ||
      normalizeText(caseAz) === normalizedSearch ||
      normalizeText(item.mandant_az) === normalizedSearch;

    const matchesStadium = !stadium || item.stadium === stadium;
    const matchesStatus =
      !bearbeitungsstatus ||
      item.bearbeitungsstatus === bearbeitungsstatus ||
      item.bearb_status === bearbeitungsstatus;
    const matchesYear =
      !yearFilter ||
      caseYear === yearFilter ||
      normalizeText(caseAz).startsWith(normalizeText(yearFilter));
    const matchesOpen = !nurOffene || !item.is_closed;

    return (
      matchesSearch &&
      matchesStadium &&
      matchesStatus &&
      matchesYear &&
      matchesOpen
    );
  });

  return sortCases(filtered, sortBy);
};

const buildExportCsv = (items = []) => {
  const headers = [
    "AZ",
    "Mandanten-AZ",
    "Schuldner",
    "Ort",
    "Stadium",
    "Bearbeitungsstatus",
    "Übernommene HF",
    "Offene HF",
    "Auslagen",
    "NK",
    "Letzter Stand",
    "Letztes Update",
    "VA-Datum",
    "EV-Datum",
    "Abschlussdatum",
    "Offen",
  ];

  const rows = items.map((item) => [
    getCaseAz(item) || "",
    item.mandant_az || "",
    item.schuldner || "",
    item.ort || "",
    item.stadium || "",
    item.bearbeitungsstatus || item.bearb_status || "",
    item.uebern_hf ?? 0,
    item.offene_hf ?? 0,
    item.uebern_kosten ?? 0,
    item.nk ?? 0,
    item.last_event_text || "",
    item.last_event_date || "",
    item.vadatum || "",
    item.evdatum || "",
    item.abschlussdatum || "",
    item.is_closed ? "Nein" : "Ja",
  ]);

  return [headers, ...rows]
    .map((row) =>
      row.map((val) => `"${String(val ?? "").replace(/"/g, '""')}"`).join(";")
    )
    .join("\n");
};

const downloadCsvFile = (filename, csvContent) => {
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const MandantCases = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [stadium, setStadium] = useState("");
  const [bearbeitungsstatus, setBearbeitungsstatus] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [nurOffene, setNurOffene] = useState(true);

  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({
    offeneAkten: 0,
    offeneHf: 0,
    uebernommeneHf: 0,
    uebernommeneKosten: 0,
    nkGesamt: 0,
    letzteAktualisierung: null,
  });

  const [expandedRows, setExpandedRows] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("last_event_date_desc");
  const [exportLoading, setExportLoading] = useState(false);
  const [nextToken, setNextToken] = useState("");
  const [listInfo, setListInfo] = useState({
    returnedCases: 0,
    limit: 200,
  });
  const didInitialLoadRef = useRef(false);

  const toggleRow = (caseId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [caseId]: !prev[caseId],
    }));
  };

  const loadStats = async (companyId, fallbackCases = []) => {
    try {
      setStatsLoading(true);

      const statsUrl = `${API_BASE}/mandant/cases/stats`;

      const statsRes = await fetch(statsUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-company-id": companyId,
        },
      });

      if (!statsRes.ok) {
        throw new Error(`Stats konnten nicht geladen werden (${statsRes.status}).`);
      }

      const statsData = await statsRes.json();
      setStats(normalizeStats(statsData, fallbackCases));
    } catch (statsErr) {
      console.warn("Stats fallback fehlgeschlagen", statsErr);
      setStats(normalizeStats({}, fallbackCases));
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchAllCasesForExport = async ({ exportMode = "filtered" } = {}) => {
    const companyId = getCompanyId();

    if (!companyId) {
      throw new Error("Keine company_id gefunden. Bitte erneut einloggen.");
    }

    let exportNextToken = "";
    let allNormalizedCases = [];

    do {
      const casesQuery = buildQuery({
        limit: 200,
        search: search || undefined,
        az_year: yearFilter || undefined,
        next_token: exportNextToken || undefined,
      });

      const casesUrl = `${API_BASE}/mandant/cases?${casesQuery}`;
      const casesRes = await fetch(casesUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-company-id": companyId,
        },
      });

      if (!casesRes.ok) {
        const errorText = await casesRes.text();
        console.error("MANDANT CASES EXPORT ERROR", errorText);
        throw new Error(`Export konnte nicht geladen werden (${casesRes.status}).`);
      }

      const casesData = await casesRes.json();
      const rawItems = Array.isArray(casesData)
        ? casesData
        : Array.isArray(casesData.items)
        ? casesData.items
        : Array.isArray(casesData.cases)
        ? casesData.cases
        : [];

      const normalizedBatch = rawItems.map((item, index) =>
        normalizeCase(item, allNormalizedCases.length + index)
      );

      allNormalizedCases = [...allNormalizedCases, ...normalizedBatch];
      exportNextToken = casesData?.next_token || "";
    } while (exportNextToken);

    if (exportMode === "all") {
      return sortCases(allNormalizedCases, sortBy);
    }

    return applyClientFilters({
      items: allNormalizedCases,
      search,
      stadium,
      bearbeitungsstatus,
      yearFilter,
      nurOffene,
      sortBy,
    });
  };

  const loadCases = async ({ requestedNextToken = "", resetPage = true } = {}) => {
    try {
      setLoading(true);
      setError("");

      const companyId = getCompanyId();

      if (!companyId) {
        throw new Error("Keine company_id gefunden. Bitte erneut einloggen.");
      }

      const casesQuery = buildQuery({
        limit: 200,
        search: search || undefined,
        az_year: yearFilter || undefined,
        next_token: requestedNextToken || undefined,
      }); 



      const casesUrl = `${API_BASE}/mandant/cases?${casesQuery}`;

      const casesRes = await fetch(casesUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-company-id": companyId,
        },
      });

      if (!casesRes.ok) {
        const errorText = await casesRes.text();
        console.error("MANDANT CASES ERROR", errorText);
        throw new Error(
          `Akten konnten nicht geladen werden (${casesRes.status}).`
        );
      }

      const casesData = await casesRes.json();

      const rawItems = Array.isArray(casesData)
        ? casesData
        : Array.isArray(casesData.items)
        ? casesData.items
        : Array.isArray(casesData.cases)
        ? casesData.cases
        : [];

      const normalizedCases = rawItems.map((item, index) =>
        normalizeCase(item, index)
      );

      setCases(normalizedCases);
      setExpandedRows({});
      setNextToken(casesData?.next_token || "");
      setListInfo({
        returnedCases: Number(
          casesData?.summary?.returned_cases || normalizedCases.length || 0
        ),
        limit: Number(casesData?.summary?.limit || 200),
      });

      if (resetPage) {
        setPage(1);
      }

      loadStats(companyId, normalizedCases);
    } catch (err) {
      console.error("MandantCases load error", err);
      setCases([]);
      setNextToken("");
      setListInfo({
        returnedCases: 0,
        limit: 200,
      });
      setStats({
        offeneAkten: 0,
        offeneHf: 0,
        uebernommeneHf: 0,
        uebernommeneKosten: 0,
        nkGesamt: 0,
        letzteAktualisierung: null,
      });
      setError(err.message || "Akten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCases({ requestedNextToken: "", resetPage: true });
    didInitialLoadRef.current = true;
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, stadium, bearbeitungsstatus, yearFilter, nurOffene, sortBy]);

  const filteredCases = useMemo(() => {
    return applyClientFilters({
      items: cases,
      search,
      stadium,
      bearbeitungsstatus,
      yearFilter,
      nurOffene,
      sortBy,
    });
  }, [cases, search, stadium, bearbeitungsstatus, yearFilter, nurOffene, sortBy]);

  const stadiumOptions = [
    ...new Set(cases.map((item) => item.stadium).filter(Boolean)),
  ];

  const statusOptions = [
    ...new Set(
      cases
        .map((item) => item.bearbeitungsstatus || item.bearb_status)
        .filter(Boolean)
    ),
  ];

  const yearOptions = [
    ...new Set(cases.map((item) => getCaseYear(item)).filter((value) => /^\d{4}$/.test(value))),
  ].sort((a, b) => b.localeCompare(a, "de"));

  const totalPages = Math.max(1, Math.ceil(filteredCases.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const paginatedCases = filteredCases.slice(pageStart, pageEnd);

  const handleExportFilteredCSV = async () => {
    try {
      setExportLoading(true);
      const exportCases = await fetchAllCasesForExport({ exportMode: "filtered" });

      if (!exportCases || exportCases.length === 0) {
        alert("Keine gefilterten Akten zum Export vorhanden.");
        return;
      }

      downloadCsvFile(
        "akten_gefiltert_export.csv",
        buildExportCsv(exportCases)
      );
    } catch (err) {
      console.error("MandantCases export filtered error", err);
      alert(err.message || "Export der gefilterten Liste fehlgeschlagen.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAllCSV = async () => {
    try {
      setExportLoading(true);
      const exportCases = await fetchAllCasesForExport({ exportMode: "all" });

      if (!exportCases || exportCases.length === 0) {
        alert("Keine Akten zum Export vorhanden.");
        return;
      }

      downloadCsvFile(
        "akten_gesamt_export.csv",
        buildExportCsv(exportCases)
      );
    } catch (err) {
      console.error("MandantCases export all error", err);
      alert(err.message || "Export der Gesamtliste fehlgeschlagen.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleReload = () => {
    loadCases({ requestedNextToken: "", resetPage: true });
  };

  const handleLoadNextBatch = () => {
    if (!nextToken) return;
    loadCases({ requestedNextToken: nextToken, resetPage: true });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-[1320px] mx-auto px-8 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-3xl font-bold text-gray-900">
              Aktenübersicht
            </div>
            <div className="mt-1 text-sm text-gray-500">
              Aktueller Stand Ihrer Inkassoakten
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Badge kind="yellow">Mandantensicht</Badge>
            <button
              type="button"
              onClick={() => navigate("/company-dashboard")}
              className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Zurück zum Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1320px] mx-auto px-8 py-10">
        {loading && (
          <div className="rounded-2xl bg-white border shadow-sm p-6 mb-8">
            <div className="text-gray-700 font-medium">Lade Akten ...</div>
            <div className="mt-2 text-sm text-gray-500">
              Die aktuelle Aktenliste wird geladen.
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl bg-red-50 border border-red-200 shadow-sm p-5 mb-8">
            <div className="text-red-800 font-semibold">Fehler</div>
            <div className="mt-1 text-sm text-red-700">{error}</div>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5 mb-8">
              <StatCard
                title="Offene Akten"
                value={statsLoading ? "…" : stats.offeneAkten}
                hint="Gesamtbestand offen"
                kind="yellow"
              />
              <StatCard
                title="Offene HF"
                value={statsLoading ? "…" : formatCurrency(stats.offeneHf, "EUR")}
                hint="Gesamtbestand Hauptforderungen"
                kind="green"
              />
              <StatCard
                title="Übernommene HF"
                value={
                  statsLoading ? "…" : formatCurrency(stats.uebernommeneHf, "EUR")
                }
                hint="Gesamtbestand Hauptforderungen"
                kind="blue"
              />
              <StatCard
                title="Auslagen"
                value={
                  statsLoading
                    ? "…"
                    : formatCurrency(stats.uebernommeneKosten, "EUR")
                }
                hint="Gesamtbestand Auslagen"
                kind="default"
              />
              <StatCard
                title="NK"
                value={statsLoading ? "…" : formatCurrency(stats.nkGesamt, "EUR")}
                hint="Gesamtbestand Nebenkosten"
                kind="red"
              />
            </div>

            <div className="grid grid-cols-1 mb-8">
              <StatCard
                title="Letzte Aktualisierung"
                value={
                  statsLoading ? "…" : formatDateTime(stats.letzteAktualisierung)
                }
                hint="Zeitpunkt des letzten Imports"
                kind="blue"
              />
            </div>

            <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-6 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sortierung
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white"
                  >
                    <option value="last_event_date_desc">
                      Letztes Update absteigend
                    </option>
                    <option value="last_event_date_asc">
                      Letztes Update aufsteigend
                    </option>
                    <option value="az_asc">AZ aufsteigend</option>
                    <option value="az_desc">AZ absteigend</option>
                    <option value="schuldner_asc">Schuldner A–Z</option>
                    <option value="schuldner_desc">Schuldner Z–A</option>
                    <option value="offene_hf_desc">Offene HF absteigend</option>
                    <option value="offene_hf_asc">Offene HF aufsteigend</option>
                    <option value="nk_desc">NK absteigend</option>
                    <option value="nk_asc">NK aufsteigend</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Suche
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="AZ, Mandanten-AZ, Schuldner, Ort ..."
                      className="w-full border border-gray-300 rounded-xl px-4 py-3"
                    />
                    <button
                      type="button"
                      onClick={handleReload}
                      className="px-5 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-800"
                    >
                      Laden
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stadium
                  </label>
                  <select
                    value={stadium}
                    onChange={(e) => setStadium(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white"
                  >
                    <option value="">Alle</option>
                    {stadiumOptions.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bearbeitungsstatus
                  </label>
                  <select
                    value={bearbeitungsstatus}
                    onChange={(e) => setBearbeitungsstatus(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white"
                  >
                    <option value="">Alle</option>
                    {statusOptions.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    AZ-Jahr
                  </label>
                  <input
                    type="text"
                
                    value={yearFilter}
                    onChange={(e) =>
                      setYearFilter(e.target.value.replace(/\D/g, "").slice(0, 4))
                    }
                    placeholder="z. B. 2026"
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white"
                  />
                
                </div>

                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={nurOffene}
                      onChange={(e) => setNurOffene(e.target.checked)}
                    />
                    <span>Nur offene Akten anzeigen</span>
                  </label>
                </div>
              </div>

              <div className="mt-5 flex justify-between items-center gap-3 flex-wrap">
                <div className="text-sm text-gray-500">
                  Geladen: {listInfo.returnedCases} Fälle pro Abruf (Limit {listInfo.limit})
                </div>

                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={handleExportFilteredCSV}
                    disabled={exportLoading}
                    className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {exportLoading ? "Export läuft ..." : "Aktuelle Auswahl herunterladen"}
                  </button>

                  <button
                    type="button"
                    onClick={handleExportAllCSV}
                    disabled={exportLoading}
                    className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Gesamtliste herunterladen
                  </button>

                  <button
                    type="button"
                    onClick={handleLoadNextBatch}
                    disabled={!nextToken || loading}
                    className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Nächste 200 laden
                  </button>
                </div>
              </div>
            </div>

            {filteredCases.length === 0 ? (
              <EmptyState
                title="Keine Akten gefunden"
                text="Entweder liegt noch kein Import vor oder die aktuelle Filterkombination liefert keine Treffer."
              />
            ) : (
              <>
                <div className="flex items-center justify-between mb-4 px-1">
                  <div className="text-sm text-gray-500">
                    Zeige {pageStart + 1} bis{" "}
                    {Math.min(pageEnd, filteredCases.length)} von{" "}
                    {filteredCases.length} geladenen Akten
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={safePage <= 1}
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm disabled:opacity-50"
                    >
                      Zurück
                    </button>
                    <div className="text-sm text-gray-600">
                      Seite {safePage} / {totalPages}
                    </div>
                    <button
                      type="button"
                      disabled={safePage >= totalPages}
                      onClick={() =>
                        setPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm disabled:opacity-50"
                    >
                      Weiter
                    </button>
                  </div>
                </div>

                <div className="max-w-[1280px] rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-[1200px] w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr className="text-left text-gray-600">
                          <th className="px-5 py-4 font-semibold">AZ</th>
                          <th className="px-5 py-4 font-semibold">Mandanten-AZ</th>
                          <th className="px-5 py-4 font-semibold">Schuldner</th>
                          <th className="px-5 py-4 font-semibold">Ort</th>
                          <th className="px-5 py-4 font-semibold">Stadium</th>
                          <th className="px-5 py-4 font-semibold">Bearbeitungsstatus</th>
                          <th className="px-5 py-4 font-semibold text-right">
                            Übernommene HF
                          </th>
                          <th className="px-5 py-4 font-semibold text-right">
                            Offene HF
                          </th>
                          <th className="px-5 py-4 font-semibold text-right">
                            NK
                          </th>
                          <th className="px-5 py-4 font-semibold text-right"></th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 bg-white">
                        {paginatedCases.map((item, index) => {
                          const rowKey =
                            item.case_id || `${item.az || "case"}-${index}`;
                          const isExpanded = !!expandedRows[rowKey];

                          return (
                            <React.Fragment key={rowKey}>
                              <tr
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={() => toggleRow(rowKey)}
                              >
                                <td className="px-5 py-4 align-top font-medium text-gray-900">
                                  {item.az || "-"}
                                </td>
                                <td className="px-5 py-4 align-top text-gray-700">
                                  {item.mandant_az || "-"}
                                </td>
                                <td className="px-5 py-4 align-top text-gray-900">
                                  {item.schuldner || "-"}
                                </td>
                                <td className="px-5 py-4 align-top text-gray-700">
                                  {item.ort || "-"}
                                </td>
                                <td className="px-5 py-4 align-top">
                                  <Badge kind={getStadiumKind(item.stadium)}>
                                    {item.stadium || "-"}
                                  </Badge>
                                </td>
                                <td className="px-5 py-4 align-top">
                                  <Badge kind={getBearbeitungsstatusKind(item.bearbeitungsstatus)}>
                                    {item.bearbeitungsstatus || "-"}
                                  </Badge>
                                </td>
                                <td className="px-5 py-4 align-top text-right font-medium text-gray-900">
                                  {formatCurrency(item.uebern_hf || 0, "EUR")}
                                </td>
                                <td className="px-5 py-4 align-top text-right font-medium text-gray-900">
                                  {formatCurrency(item.offene_hf || 0, "EUR")}
                                </td>
                                <td className="px-5 py-4 align-top text-right font-medium text-gray-900">
                                  {formatCurrency(item.nk || 0, "EUR")}
                                </td>
                                <td className="px-5 py-4 align-top text-right text-gray-500">
                                  {isExpanded ? "–" : "+"}
                                </td>
                              </tr>

                              {isExpanded && (
                                <tr className="bg-gray-50">
                                  <td colSpan={10} className="px-6 py-6">
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                      <div className="rounded-2xl border border-gray-200 bg-white p-4">
                                        <div className="mb-3 text-sm font-semibold text-gray-900">
                                          Beträge
                                        </div>
                                        <div className="space-y-2 text-sm">
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Übernommene HF
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {formatCurrency(item.uebern_hf || 0)}
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Offene HF
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {formatCurrency(item.offene_hf || 0)}
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Auslagen
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {formatCurrency(item.uebern_kosten || 0)}
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              NK
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {formatCurrency(item.nk || 0)}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="rounded-2xl border border-gray-200 bg-white p-4">
                                        <div className="mb-3 text-sm font-semibold text-gray-900">
                                          Verfahren
                                        </div>
                                        <div className="space-y-2 text-sm">
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Letzter Stand
                                            </span>
                                            <span className="font-medium text-gray-900 text-right">
                                              {item.last_event_text || "-"}
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Letzter Vorgang
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {formatDate(item.letzter_vorgang_datum || item.last_event_date)}
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Seit letztem Vorgang
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {item.tage_seit_letztem_vorgang !== null && item.tage_seit_letztem_vorgang !== undefined
                                                ? `${item.tage_seit_letztem_vorgang} Tage`
                                                : "-"}
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              VA-Datum
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {formatDate(item.vadatum)}
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              EV-Datum
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {formatDate(item.evdatum)}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="rounded-2xl border border-gray-200 bg-white p-4">
                                        <div className="mb-3 text-sm font-semibold text-gray-900">
                                          Status
                                        </div>
                                        <div className="space-y-2 text-sm">
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">Stadium</span>
                                            <span>
                                              <Badge kind={getStadiumKind(item.stadium)}>
                                                {item.stadium || "-"}
                                              </Badge>
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Bearbeitungsstatus
                                            </span>
                                            <span>
                                              <Badge kind={getBearbeitungsstatusKind(item.bearbeitungsstatus)}>
                                                {item.bearbeitungsstatus || "-"}
                                              </Badge>
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Technischer Status
                                            </span>
                                            <span>
                                              <Badge kind={getStatusKind(item.bearb_status)}>
                                                {item.bearb_status || "-"}
                                              </Badge>
                                            </span>
                                          </div>
                                          {item.status_text ? (
                                            <div className="pt-2 text-sm text-gray-600 border-t border-gray-100">
                                              {item.status_text}
                                            </div>
                                          ) : null}
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Offen
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {item.offen_text || (item.is_closed ? "Nein" : "Ja")}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MandantCases;